import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import { Box } from '@honda-canada/design-system-react';
import MapBounds from './MapBounds';
import themeStyles from './EmbeddedGoogleMap.styles';
import GoogleMapRefContext from '../GoogleMapRefContext';
import isSSR from '../../utils/isSSR';

const Overlay = themeStyles.apply(Box, 'Overlay');

const EmbeddedGoogleMap = ({ lat, lng, zoom, width, height, mapControlOptions, draggable, isMapLocked }) => {
  const mapRef = useContext(GoogleMapRefContext);

  if (isSSR()) return null;

  const center = { lat, lng };
  const mapContainerStyle = { width, height };
  // flag to disable map dragging
  const gestureHandling = draggable ? 'auto' : 'none';
  const mapOptions = isMapLocked
    ? { ...mapControlOptions, gestureHandling: 'none', cameraControl: false, zoomControl: false, streetViewControl: false }
    : { ...mapControlOptions, gestureHandling };

  const getPixelPositionOffset = (w, h) => ({
    x: -(w / 2),
    y: -(h / 2),
  });

  const onMapLoad = map => {
    // set initial map view on load
    map.panTo(center);
    map.setZoom(zoom);
    // store a reference of map in context
    mapRef.current = map;
  };

  const onMapUnmount = () => {
    mapRef.current = null;
  };

  return (
    <GoogleMap mapContainerStyle={mapContainerStyle} options={mapOptions} onLoad={onMapLoad} onUnmount={onMapUnmount}>
      {!draggable && (
        <OverlayView
          position={center}
          mapPaneName={OverlayView.OVERLAY_LAYER}
          getPixelPositionOffset={getPixelPositionOffset}
        >
          <Overlay data-testid="cy-grey-overlay" />
        </OverlayView>
      )}
      <MapBounds isMapLocked={isMapLocked} />
    </GoogleMap>
  );
};

export default memo(EmbeddedGoogleMap);

EmbeddedGoogleMap.defaultProps = {
  width: '100%',
  height: '100%',
  mapControlOptions: {
    mapTypeControl: false,
    cameraControl: true,
    zoomControl: false,
    streetViewControl: true,
    fullscreenControl: false,
    scrollwheel: false,
    panControl: false,
    scaleControl: false,
    overviewMapControl: false,
  },
  draggable: true,
  isMapLocked: false,
};

EmbeddedGoogleMap.propTypes = {
  /**
   * initial latitude value for map
   */
  lat: PropTypes.number.isRequired,
  /**
   * initial longitude value for map
   */
  lng: PropTypes.number.isRequired,
  /**
   * initial zoom value for map
   */
  zoom: PropTypes.number.isRequired,
  /**
   * options object for configuration of map controls
   */
  mapControlOptions: PropTypes.shape({}),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  draggable: PropTypes.bool,
  isMapLocked: PropTypes.bool,
};
